'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (
      request: Parameters<
        typeof import('@eliadmin/core/src/api/newsletter/newsletter-api').newsletterSubscribe
      >[0],
    ) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(request);
    },
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('bg-primary/15 p-4 sm:p-12', 'ea-homepage-newsletter')}>
      <form
        className={cn('flex flex-col gap-6 py-2 md:flex-row', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'flex w-full flex-col gap-2 md:max-w-[45%]',
            'ea-homepage-newsletter__form-title',
          )}
        >
          <div
            className={cn(
              'relative flex items-center justify-start gap-x-3 md:left-[-15px]',
              'ea-homepage-newsletter-subtitle',
            )}
          >
            <Image src={'/images/heartBeat.svg'} alt="" height={20} width={20} unoptimized />
            <span className="text-[16px] font-bold text-primary">NEWSLETTER</span>
          </div>
          <h2
            data-ea-visual-tag="h4"
            className={cn('max-w-sm text-2xl font-bold', 'ea-homepage-newsletter__form-title-main')}
          >
            {t('text610')}
          </h2>
          <p
            className={cn(
              'text-sm text-muted-foreground',
              'ea-homepage-newsletter__form-title-sub',
            )}
          >
            {t('text609')}
          </p>
        </div>
        <div
          className={cn(
            'flex w-full flex-col justify-center space-y-3 ',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex flex-col items-center gap-5 md:flex-row')}>
            <Input
              aria-label={systemLabels('email')}
              id="newsletter-email"
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={e => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'border-none bg-white pr-1 text-muted-foreground',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
              }}
              afterOuter={
                <Button
                  type="submit"
                  className={cn(
                    'w-full rounded-[12px] border-none bg-primary px-2 text-primary-foreground md:max-w-[150px]',
                    'ea-homepage-newsletter__form-item-email-submit-button',
                  )}
                  disabled={email.length === 0 || !checked || isSubscribed}
                  isLoading={subscribeMutation.isPending}
                >
                  {t('text182')}
                </Button>
              }
            />
          </div>
          {isSubscribed && <span className={cn('font-semibold text-primary')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id="newsletter-gdpr"
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor="newsletter-gdpr"
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                {t('text264')}&nbsp;
                <a
                  href={t('text391')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
